/**
 * Displays a single extracted NPS account.
 *
 * Compact (always visible) — the 5 snapshot cards:
 *   1. NPS Snapshot          PRAN · status · tier · registered
 *   2. Retirement Corpus     value · contributions · gain/loss · XIRR
 *   3. Investment Allocation E / C / G / A stacked bar
 *   4. Tax Benefit Tracker   80CCD framing + contribution split
 *   5. Nominee & readiness    nominee name + share
 *
 * Show more — the detailed intelligence layer:
 *   1. Portfolio breakdown   scheme-wise holdings (PFM, NAV, units, value)
 *   2. Contribution summary  employee vs employer + count
 *   3. Returns & performance XIRR + gain/loss restated
 *   4. Tax intelligence      80CCD(1) / (1B) / (2) explainer
 *
 * Tax numbers are NOT computed against FY limits — a statement shows
 * lifetime contributions, not this-year's, so we present the framework
 * plus the extracted amounts and let the advisor close the loop.
 */
import * as React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@finmin/ui';
import {
  Banknote,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Landmark,
  PiggyBank,
  ReceiptText,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import type { NpsAccountExtracted } from '@/api/profile-documents';
import {
  formatDate,
  formatINR,
  formatINRCompact,
  formatNpsAssetClass,
  formatNpsStatus,
  formatNpsTier,
  formatPct,
  toNum,
} from '@/features/insurance/format';

interface Props {
  account: NpsAccountExtracted;
  defaultExpanded?: boolean;
}

export function NpsAccountCard({ account: a, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const status = formatNpsStatus(a.account_status);

  const gain = toNum(a.total_gain_loss);
  const gainPositive = gain != null && gain >= 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <PiggyBank className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <span className="truncate">National Pension System</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {a.pran ? `PRAN ${a.pran}` : 'PRAN —'}
              {a.subscriber_name ? ` · ${a.subscriber_name}` : ''}
            </p>
          </div>
          {a.is_manually_edited && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Edited
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Card 1 — Snapshot ─────────────────────────────────────────── */}
        <section className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <Stat
            label="Status"
            value={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
          />
          <Stat label="Tier" value={formatNpsTier(a.tier)} />
          <Stat label="Registered" value={formatDate(a.registration_date)} />
          <Stat
            label="XIRR"
            value={a.xirr_pct != null ? formatPct(a.xirr_pct) : '—'}
            sub="annualised"
          />
        </section>

        {/* ── Card 2 — Retirement Corpus ────────────────────────────────── */}
        <section className="rounded-md border border-border bg-secondary/30 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Banknote className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            <Label>Retirement corpus</Label>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
            <Stat label="Current value" value={formatINRCompact(a.portfolio_value)} />
            <Stat label="Contributed" value={formatINRCompact(a.total_contributions)} />
            <Stat
              label="Gain / loss"
              value={
                <span
                  className={cn(
                    'tabular-nums',
                    gain == null
                      ? ''
                      : gainPositive
                        ? 'text-success'
                        : 'text-destructive',
                  )}
                >
                  {gain == null
                    ? '—'
                    : `${gainPositive ? '+' : ''}${formatINRCompact(a.total_gain_loss)}`}
                </span>
              }
            />
          </div>
        </section>

        {/* ── Card 3 — Investment Allocation ────────────────────────────── */}
        <AllocationBar account={a} />

        {/* ── Card 4 — Tax Benefit Tracker (snapshot) ───────────────────── */}
        <section className="rounded-md border border-border p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <ReceiptText className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            <Label>Tax benefit</Label>
          </div>
          <ul className="space-y-1 text-sm text-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" aria-hidden />
              Employee contributions count under{' '}
              <span className="font-medium">80CCD(1)</span> (within ₹1.5L 80C).
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" aria-hidden />
              Extra <span className="font-medium">₹50,000</span> deduction under{' '}
              <span className="font-medium">80CCD(1B)</span> — over and above 80C.
            </li>
            {toNum(a.employer_contribution_total) ? (
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" aria-hidden />
                Employer contribution{' '}
                <span className="font-medium">
                  {formatINRCompact(a.employer_contribution_total)}
                </span>{' '}
                is deductible under <span className="font-medium">80CCD(2)</span>.
              </li>
            ) : null}
          </ul>
        </section>

        {/* ── Card 5 — Nominees ─────────────────────────────────────────── */}
        {a.nominees.length > 0 && (
          <section>
            <Label>
              <Users className="h-3 w-3 inline mr-1" aria-hidden />
              Nominees
            </Label>
            <ul className="mt-1.5 space-y-1.5">
              {a.nominees.map((n, i) => (
                <li
                  key={`${n.name ?? 'nominee'}-${i}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-foreground min-w-0 truncate">
                    <span className="font-medium">{n.name ?? '—'}</span>
                    {n.relationship && (
                      <span className="text-muted-foreground"> · {n.relationship}</span>
                    )}
                    {n.is_minor && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        minor
                        {n.guardian_name ? ` · via ${n.guardian_name}` : ''}
                      </Badge>
                    )}
                  </span>
                  <span className="text-sm font-semibold text-foreground tabular-nums ml-2 shrink-0">
                    {n.share_pct != null ? `${n.share_pct}%` : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Detailed intelligence (Show more) ─────────────────────────── */}
        {expanded && (
          <div className="space-y-5 border-t border-border pt-5">
            {/* Section 1 — Portfolio breakdown */}
            {a.scheme_holdings.length > 0 && (
              <section>
                <Label>
                  <Landmark className="h-3 w-3 inline mr-1" aria-hidden />
                  Portfolio breakdown
                </Label>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground text-left">
                        <th className="font-medium py-1 pr-3">Scheme</th>
                        <th className="font-medium py-1 pr-3">Class</th>
                        <th className="font-medium py-1 pr-3 text-right">NAV</th>
                        <th className="font-medium py-1 pr-3 text-right">Units</th>
                        <th className="font-medium py-1 text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.scheme_holdings.map((h, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="py-1.5 pr-3">
                            <div className="text-foreground">
                              {h.pfm_name ?? h.scheme_name ?? '—'}
                            </div>
                            {h.pfm_name && h.scheme_name && (
                              <div className="text-xs text-muted-foreground">
                                {h.scheme_name}
                              </div>
                            )}
                          </td>
                          <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                            {formatNpsAssetClass(h.asset_class)}
                          </td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">
                            {h.nav != null ? h.nav.toFixed(4) : '—'}
                          </td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">
                            {h.units != null ? h.units.toFixed(2) : '—'}
                          </td>
                          <td className="py-1.5 text-right tabular-nums font-medium">
                            {formatINRCompact(h.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Section 2 — Contribution summary */}
            <section>
              <Label>Contributions</Label>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                <Mini label="Employee" value={formatINRCompact(a.employee_contribution_total)} />
                <Mini label="Employer" value={formatINRCompact(a.employer_contribution_total)} />
                <Mini label="Total" value={formatINRCompact(a.total_contributions)} />
                <Mini
                  label="Transactions"
                  value={
                    a.contributions.length > 0 ? String(a.contributions.length) : '—'
                  }
                />
              </dl>
            </section>

            {/* Section 3 — Returns & performance */}
            <section>
              <Label>
                <TrendingUp className="h-3 w-3 inline mr-1" aria-hidden />
                Returns & performance
              </Label>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <Mini label="XIRR" value={formatPct(a.xirr_pct)} />
                <Mini label="Total gain/loss" value={formatINR(a.total_gain_loss)} />
                <Mini
                  label="Statement period"
                  value={
                    a.statement_from || a.statement_to
                      ? `${formatDate(a.statement_from)} – ${formatDate(a.statement_to)}`
                      : '—'
                  }
                />
              </dl>
            </section>

            {/* Section 4 — Tax intelligence */}
            <section className="rounded-md bg-secondary/40 p-3 space-y-1.5">
              <Label>Tax intelligence</Label>
              <ul className="space-y-1.5 text-xs text-muted-foreground list-disc pl-5">
                <li>
                  <span className="text-foreground">80CCD(1)</span> — your own
                  contributions sit inside the ₹1.5 lakh Section 80C ceiling.
                </li>
                <li>
                  <span className="text-foreground">80CCD(1B)</span> — an
                  additional ₹50,000 deduction exclusive to NPS, on top of 80C.
                  Maxing this is usually the single best NPS tax move.
                </li>
                <li>
                  <span className="text-foreground">80CCD(2)</span> — employer
                  contributions are deductible separately (up to 10% of salary,
                  14% for government employees) with no 80C overlap.
                </li>
                <li className="text-foreground/70">
                  Figures above are lifetime totals from this statement, not the
                  current financial year — your advisor will reconcile FY usage.
                </li>
              </ul>
            </section>

            {a.extraction_notes && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                <p>{a.extraction_notes}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Corpus: <span className="font-medium">{formatINR(a.portfolio_value)}</span>{' '}
              · Extracted {formatDate(a.extracted_at)}
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="gap-1.5 text-xs"
          >
            {expanded ? (
              <>
                Show less
                <ChevronUp className="h-3 w-3" aria-hidden />
              </>
            ) : (
              <>
                Show more
                <ChevronDown className="h-3 w-3" aria-hidden />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Allocation bar ──────────────────────────────────────────────────────────

function AllocationBar({ account: a }: { account: NpsAccountExtracted }) {
  const segments = [
    { label: 'Equity (E)', pct: toNum(a.equity_pct), cls: 'bg-primary' },
    { label: 'Corp bonds (C)', pct: toNum(a.corporate_bonds_pct), cls: 'bg-primary/60' },
    { label: 'Govt sec (G)', pct: toNum(a.govt_securities_pct), cls: 'bg-primary/30' },
    { label: 'Alternative (A)', pct: toNum(a.alternative_pct), cls: 'bg-muted-foreground/40' },
  ].filter((s) => s.pct != null && s.pct > 0) as {
    label: string;
    pct: number;
    cls: string;
  }[];

  if (segments.length === 0) return null;

  return (
    <section>
      <Label>Investment allocation</Label>
      <div className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-secondary">
        {segments.map((s) => (
          <div
            key={s.label}
            className={s.cls}
            style={{ width: `${s.pct}%` }}
            title={`${s.label}: ${s.pct}%`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5 text-xs">
            <span className={cn('h-2.5 w-2.5 rounded-sm', s.cls)} aria-hidden />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-medium text-foreground tabular-nums">{s.pct}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <div className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
        {value}
      </div>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'destructive' | 'muted';
  children: React.ReactNode;
}) {
  const Icon =
    tone === 'success' ? CheckCircle2 : tone === 'destructive' ? XCircle : Clock;
  const toneClass: Record<typeof tone, string> = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/30',
    destructive: 'bg-destructive/10 text-destructive border-destructive/20',
    muted: 'bg-secondary text-muted-foreground border-border',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        toneClass[tone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {children}
    </span>
  );
}

function Mini({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground tabular-nums">{value}</dd>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </span>
  );
}
